import axios, { AxiosResponse, CancelTokenSource } from 'axios';
import { getRefreshedToken, addItemToStorage } from '../utils';
import { SPOTIFY_TOKEN } from '../constants/storage';
import type { PlaylistData } from '../utils/csvParser';

export interface ImportProgress {
  completed: number;
  total: number;
  currentPlaylist?: string;
  successful: string[];
  failed: Array<{
    name: string;
    error: string;
  }>;
}

export interface ImportResult {
  success: boolean;
  message: string;
  progress: ImportProgress;
  cancelled?: boolean;
}

export interface ImportOptions {
  onProgress?: (progress: ImportProgress) => void;
  onPlaylistCreated?: (playlistName: string, spotifyId: string) => void;
  onError?: (playlistName: string, error: string) => void;
  retryAttempts?: number;
  delayBetweenRequests?: number;
}

class CsvImportService {
  private cancelTokenSource: CancelTokenSource | null = null;
  private isImporting = false;
  private isCancelled = false;

  /**
   * Import multiple playlists from CSV data
   */
  public async importPlaylists(
    playlistsData: PlaylistData[],
    token: string,
    refreshToken: string,
    userId: string,
    options: ImportOptions = {}
  ): Promise<ImportResult> {
    const {
      onProgress,
      onPlaylistCreated,
      onError,
      retryAttempts = 2,
      delayBetweenRequests = 500
    } = options;

    // Prevent multiple simultaneous imports
    if (this.isImporting) {
      return {
        success: false,
        message: 'Another import is already in progress',
        progress: {
          completed: 0,
          total: 0,
          successful: [],
          failed: []
        }
      };
    }

    this.isImporting = true;
    this.isCancelled = false;
    this.cancelTokenSource = axios.CancelToken.source();

    const progress: ImportProgress = {
      completed: 0,
      total: playlistsData.length,
      successful: [],
      failed: []
    };

    try {
      let currentToken = token;

      for (let i = 0; i < playlistsData.length; i++) {
        // Check if import was cancelled
        if (this.isCancelled) {
          return {
            success: false,
            message: 'Import was cancelled',
            progress,
            cancelled: true
          };
        }

        const playlist = playlistsData[i];
        progress.currentPlaylist = playlist.name;
        
        // Notify progress
        if (onProgress) {
          onProgress({ ...progress });
        }

        let success = false;
        let lastError = '';

        // Retry logic for each playlist
        for (let attempt = 0; attempt <= retryAttempts; attempt++) {
          try {
            const spotifyId = await this.createSinglePlaylist(
              playlist,
              currentToken,
              this.cancelTokenSource.token
            );

            progress.successful.push(playlist.name);
            progress.completed++;
            success = true;

            if (onPlaylistCreated) {
              onPlaylistCreated(playlist.name, spotifyId);
            }

            break; // Success, no need to retry

          } catch (error: any) {
            lastError = this.extractErrorMessage(error);

            // Handle token expiry
            if (this.isTokenExpiredError(error)) {
              try {
                const refreshResponse = await getRefreshedToken(refreshToken);
                currentToken = refreshResponse.data.access_token;
                addItemToStorage(SPOTIFY_TOKEN, currentToken);
                
                // Don't count token refresh as a retry attempt
                attempt--;
                continue;
              } catch (refreshError) {
                lastError = 'Failed to refresh authentication token';
                break;
              }
            }

            // Handle rate limiting
            if (this.isRateLimitError(error)) {
              const retryAfter = this.getRetryAfterDelay(error);
              await this.delay(retryAfter * 1000);
              continue;
            }

            // For other errors, wait before retry
            if (attempt < retryAttempts) {
              await this.delay(delayBetweenRequests * (attempt + 1));
            }
          }
        }

        if (!success) {
          progress.failed.push({
            name: playlist.name,
            error: lastError
          });
          progress.completed++;

          if (onError) {
            onError(playlist.name, lastError);
          }
        }

        // Add delay between requests to respect rate limits
        if (i < playlistsData.length - 1) {
          await this.delay(delayBetweenRequests);
        }
      }

      // Final progress update
      progress.currentPlaylist = undefined;
      if (onProgress) {
        onProgress({ ...progress });
      }

      const successCount = progress.successful.length;
      const failCount = progress.failed.length;

      if (successCount === 0) {
        return {
          success: false,
          message: `Failed to create any playlists. ${failCount} error(s) occurred.`,
          progress
        };
      } else if (failCount === 0) {
        return {
          success: true,
          message: `Successfully created ${successCount} playlist(s)!`,
          progress
        };
      } else {
        return {
          success: true,
          message: `Created ${successCount} playlist(s) successfully. ${failCount} failed.`,
          progress
        };
      }

    } catch (error: any) {
      return {
        success: false,
        message: `Import failed: ${this.extractErrorMessage(error)}`,
        progress
      };
    } finally {
      this.isImporting = false;
      this.isCancelled = false;
      this.cancelTokenSource = null;
    }
  }

  /**
   * Create a single playlist via Spotify API
   */
  private async createSinglePlaylist(
    playlist: PlaylistData,
    token: string,
    cancelToken: any
  ): Promise<string> {
    const response: AxiosResponse = await axios.post(
      'https://api.spotify.com/v1/me/playlists',
      {
        name: playlist.name,
        description: playlist.description,
        public: false // Default to private playlists for safety
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        cancelToken
      }
    );

    return response.data.id;
  }

  /**
   * Cancel ongoing import
   */
  public cancelImport(): void {
    this.isCancelled = true;
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel('Import cancelled by user');
    }
  }

  /**
   * Check if an import is currently in progress
   */
  public isImportInProgress(): boolean {
    return this.isImporting;
  }

  /**
   * Extract meaningful error message from various error types
   */
  private extractErrorMessage(error: any): string {
    if (axios.isCancel(error)) {
      return 'Request was cancelled';
    }

    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const data = error.response.data;

      switch (status) {
        case 401:
          return 'Authentication failed. Please try logging in again.';
        case 403:
          return 'Permission denied. Check your Spotify permissions.';
        case 429:
          return 'Too many requests. Please wait and try again.';
        case 400:
          if (data?.error?.message) {
            return data.error.message;
          }
          return 'Invalid request data';
        case 500:
        case 502:
        case 503:
          return 'Spotify service temporarily unavailable';
        default:
          return data?.error?.message || `HTTP ${status} error occurred`;
      }
    } else if (error.request) {
      // Request made but no response received
      return 'Network error. Please check your connection.';
    } else {
      // Something else happened
      return error.message || 'An unexpected error occurred';
    }
  }

  /**
   * Check if error is due to expired token
   */
  private isTokenExpiredError(error: any): boolean {
    return error.response?.status === 401 || 
           error.response?.data?.error?.message === 'The access token expired';
  }

  /**
   * Check if error is due to rate limiting
   */
  private isRateLimitError(error: any): boolean {
    return error.response?.status === 429;
  }

  /**
   * Get retry delay from rate limit headers
   */
  private getRetryAfterDelay(error: any): number {
    const retryAfter = error.response?.headers?.['retry-after'];
    return retryAfter ? parseInt(retryAfter, 10) : 1;
  }

  /**
   * Promise-based delay utility
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Validate authentication before starting import
   */
  public async validateAuthentication(token: string): Promise<{
    valid: boolean;
    error?: string;
  }> {
    try {
      await axios.get('https://api.spotify.com/v1/me', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        timeout: 10000
      });

      return { valid: true };
    } catch (error: any) {
      return {
        valid: false,
        error: this.extractErrorMessage(error)
      };
    }
  }

  /**
   * Get user's current playlist count and limits
   */
  public async getUserPlaylistInfo(token: string): Promise<{
    totalPlaylists: number;
    ownedPlaylists: number;
    canCreateMore: boolean;
    error?: string;
  }> {
    try {
      const response = await axios.get('https://api.spotify.com/v1/me/playlists?limit=1', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const totalPlaylists = response.data.total || 0;
      
      // Spotify doesn't have a hard limit on playlist creation for most users
      // We'll use a reasonable default warning threshold
      const maxRecommended = 500;
      
      return {
        totalPlaylists,
        ownedPlaylists: totalPlaylists, // Simplified - actual owned count would need more API calls
        canCreateMore: totalPlaylists < maxRecommended
      };
    } catch (error: any) {
      return {
        totalPlaylists: 0,
        ownedPlaylists: 0,
        canCreateMore: true,
        error: this.extractErrorMessage(error)
      };
    }
  }
}

// Export singleton instance
export const csvImportService = new CsvImportService();
export default csvImportService;
